import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, Lock, Heart } from "lucide-react";

const TONES = [
  { key: "professional", label: "Professional" },
  { key: "warm", label: "Warm" },
  { key: "enthusiastic", label: "Enthusiastic" },
  { key: "concise", label: "Concise" },
];

export default function ThankYouLetter() {
  const { user } = useAuth();
  const loc = useLocation();
  const preApp = loc.state?.application;
  const [company, setCompany] = useState(preApp?.company || "");
  const [jobTitle, setJobTitle] = useState(preApp?.job_title || "");
  const [interviewer, setInterviewer] = useState("");
  const [jobDesc, setJobDesc] = useState("");
  const [notes, setNotes] = useState(preApp?.notes || "");
  const [tone, setTone] = useState("professional");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const isPro = user?.plan === "pro";

  const run = async () => {
    if (!isPro) { toast.error("Upgrade to Pro to generate thank-you letters"); return; }
    if (!notes.trim() || !jobDesc.trim()) { toast.error("Add interview notes and the job description"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/ai/thank-you-letter", {
        interview_notes: notes, job_description: jobDesc, company, job_title: jobTitle,
        interviewer_name: interviewer || null, tone,
      });
      setResult(data);
      toast.success("Thank-you letter ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI request failed");
    } finally { setBusy(false); }
  };

  const copyEmail = () => {
    if (!result?.email_body) return;
    const text = (result.subject_line ? `Subject: ${result.subject_line}\n\n` : "") + result.email_body;
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="label-eyebrow">AI Thank-You Letter</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Follow up like you've already got the offer</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">Paste your interview notes — the AI writes a personalized follow-up email that references what was actually discussed.</p>
        </div>
        {!isPro && (
          <Link to="/pricing" className="nb-btn bg-[#ec4899] text-white px-5 py-2.5 inline-flex items-center gap-2">
            <Lock className="w-4 h-4" /> Unlock with Pro
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="nb-card p-6">
          <h2 className="text-xl font-bold">Inputs</h2>
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="ty-company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company"
                className="border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca]" />
              <input data-testid="ty-title" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="Job title"
                className="border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca]" />
            </div>
            <input data-testid="ty-interviewer" value={interviewer} onChange={(e) => setInterviewer(e.target.value)} placeholder="Interviewer name (optional)"
              className="w-full border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca]" />
            <div>
              <label className="text-xs font-bold uppercase tracking-widest">Tone</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {TONES.map((t) => (
                  <button key={t.key} data-testid={`ty-tone-${t.key}`} onClick={() => setTone(t.key)}
                    className={`nb-pill ${tone === t.key ? "bg-[#ec4899] text-white" : "bg-white"}`}>{t.label}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest">Interview notes</label>
              <textarea data-testid="ty-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={8}
                placeholder="What was discussed? Names, topics, moments of chemistry, questions asked, anything you want referenced…"
                className="mt-1 w-full border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca] text-sm" />
              <p className="mt-1 text-xs text-gray-500">The AI ONLY uses what you write here — it won't invent details.</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest">Job description</label>
              <textarea data-testid="ty-jd" value={jobDesc} onChange={(e) => setJobDesc(e.target.value)} rows={6}
                className="mt-1 w-full border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca] text-sm" />
            </div>
            <button data-testid="ty-run" onClick={run} disabled={busy}
              className="nb-btn bg-[#ec4899] text-white px-5 py-3 w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Writing…</> : <><Sparkles className="w-4 h-4" /> Draft thank-you email</>}
            </button>
          </div>
        </div>

        <div className="nb-card p-6 min-h-[300px]" data-testid="ty-result">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Email draft</h2>
            {result && <button data-testid="ty-copy" onClick={copyEmail} className="nb-btn bg-white px-3 py-1.5 text-sm inline-flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Copy</button>}
          </div>
          {!result ? (
            <div className="mt-10 text-center text-gray-500">
              <Heart className="w-10 h-10 mx-auto text-[#ec4899]" />
              <p className="mt-3 font-bold">Your follow-up email will appear here.</p>
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              {result.subject_line && (
                <div>
                  <div className="label-eyebrow">Subject line</div>
                  <p className="mt-1 font-bold">{result.subject_line}</p>
                </div>
              )}
              <div>
                <div className="label-eyebrow">Email body</div>
                <pre className="mt-2 whitespace-pre-wrap font-sans text-sm leading-relaxed bg-[#FEFCE8] p-4 border-2 border-black rounded-lg">{result.email_body}</pre>
              </div>
              {result.highlights_referenced?.length > 0 && (
                <div>
                  <div className="label-eyebrow">References from your notes</div>
                  <ul className="mt-2 space-y-1 text-sm list-disc ml-5">
                    {result.highlights_referenced.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
